import React, { useEffect, useRef, useState } from 'react';
import { vendorService } from '../../services';
import { useDialog } from '../../context/DialogContext';
import { ClubPanel, PanelHead, Switch, TagPill, MUTED, SUBTLE, RULE, CHIP, INK, CYAN } from '../club/clubUi';
import { orderTabs } from './proPageModel';

/**
 * PageTabsSettings — TABS ON YOUR PAGE in the dashboard SETTINGS tab.
 *
 * One row per tab the page's kind offers (the union of ProPage's desktop + mobile tab maps,
 * plus `events`, which every kind has, plus CLUB when one is linked). Each row has:
 *  - a grip — drag it (mouse or finger; pointer events, so it works on a phone) or use ▲ ▼ to
 *    put the tab where you want it. One order serves desktop and phone alike.
 *  - OPENS HERE — the tab `/pro/{slug}` lands on. One tab wears it; a hidden tab can't.
 *  - a switch — off hides the tab for visitors. `about` is listed but locked on.
 * Every change saves at once through `PUT /pro/pages/{id}/tabs` (only the part that changed)
 * and ticks SAVED; a refusal puts things back and says why. Nothing is deleted by hiding.
 * Every tab is its own link: `/pro/{slug}/{tab}` — the row prints it.
 */
export const TAB_META = {
  about: ['ABOUT', 'Who you are. Always on.'],
  events: ['EVENTS', 'Events linked to this page.'],
  menu: ['MENU', 'The full menu and TOP THINGS.'],
  drinks: ['DRINKS', 'What is on tap, snacks, happy hour.'],
  services: ['SERVICES', 'What you do and what it costs.'],
  hire: ['HIRE', 'Hire this room — capacity, rate, sound.'],
  offers: ['OFFERS', 'Live vendor offers guests can claim.'],
  hours: ['HOURS', 'Opening hours and OPEN NOW.'],
  where: ['WHERE', 'Address, neighbourhood, parking.'],
  photos: ['PHOTOS', 'Your gallery.'],
  faces: ['FACES', 'The people behind the page.'],
  club: ['CLUB', 'The club linked to this page.'],
};

/** Tabs a kind offers, in the built-in order the public page draws them. Mirrors ProPage's maps. */
export const PAGE_TABS = {
  bookable: ['about', 'events', 'photos', 'services', 'offers', 'faces'],
  eatery: ['about', 'events', 'menu', 'offers', 'hours', 'where', 'photos'],
  bar: ['about', 'events', 'drinks', 'offers', 'hours', 'where', 'photos'],
  venue: ['about', 'events', 'hire', 'offers', 'hours', 'where', 'photos'],
  shop: ['about', 'events', 'services', 'offers', 'hours', 'where', 'photos'],
};
export const tabsForKind = (kind, { linkedClub = false } = {}) => [...(PAGE_TABS[kind] || PAGE_TABS.bookable), ...(linkedClub ? ['club'] : [])];

const move = (arr, from, to) => { const n = [...arr]; const [m] = n.splice(from, 1); n.splice(to, 0, m); return n; };

export default function PageTabsSettings({ pageId, kind, slug, hiddenTabs, tabOrder, defaultTab, linkedClub = false, onSaved, phone = false }) {
  const { showAlert } = useDialog();
  const base = tabsForKind(kind, { linkedClub });
  const [hidden, setHidden] = useState(() => new Set((hiddenTabs || []).filter((t) => t !== 'about')));
  const [order, setOrder] = useState(() => orderTabs(base, tabOrder));
  const [home, setHome] = useState(defaultTab || null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [drag, setDrag] = useState(null); // { tab, y, over }
  const rowsRef = useRef({});
  const savedTimer = useRef(null);
  const seqRef = useRef(0);
  useEffect(() => { setHidden(new Set((hiddenTabs || []).filter((t) => t !== 'about'))); }, [hiddenTabs]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOrder(orderTabs(tabsForKind(kind, { linkedClub }), tabOrder)); }, [kind, linkedClub, tabOrder && tabOrder.join(',')]);
  useEffect(() => { setHome(defaultTab || null); }, [defaultTab]);
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  if (!pageId) return null;

  const visible = order.filter((t) => !hidden.has(t));
  const homeTab = home && visible.includes(home) ? home : visible[0];

  // Every change saves only its own part; the newest request owns the SAVED / error outcome,
  // and a refusal reverts what that request changed.
  const save = async (parts, revert) => {
    setSaving(true); setSaved(false);
    const seq = ++seqRef.current;
    const r = await vendorService.setTabs(pageId, parts);
    if (r.success) {
      onSaved?.(r.data || parts);
      if (seq !== seqRef.current) return;
      setSaving(false);
      setSaved(true);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 1800);
      return;
    }
    revert?.();
    if (seq === seqRef.current) setSaving(false);
    await showAlert({ title: 'TABS ON YOUR PAGE', message: r.status === 404 ? 'Tab settings are not available yet.' : (r.error || 'Could not save that.'), type: 'error' });
  };

  const toggle = (tab, on) => {
    if (tab === 'about') return;
    const next = new Set(hidden);
    if (on) next.delete(tab); else next.add(tab);
    setHidden(next);
    const list = order.filter((t) => next.has(t));
    const parts = { hiddenTabs: list };
    // Hiding the tab the page opens on hands that job to the first visible tab.
    if (!on && homeTab === tab) { parts.defaultTab = ''; setHome(null); }
    save(parts, () => setHidden((h) => { const back = new Set(h); if (on) back.add(tab); else back.delete(tab); return back; }));
  };

  const reorder = (next) => {
    if (next.join(',') === order.join(',')) return;
    const prev = order;
    setOrder(next);
    save({ tabOrder: next }, () => setOrder(prev));
  };
  const nudge = (i, d) => { const j = i + d; if (j < 0 || j >= order.length) return; reorder(move(order, i, j)); };

  const makeHome = (tab) => {
    if (hidden.has(tab) || tab === homeTab) return;
    const prev = home;
    setHome(tab);
    save({ defaultTab: tab }, () => setHome(prev));
  };

  // Drag: the grip takes the pointer, the row follows the finger, rows it passes slide out of
  // the way, and the order is saved on release. Pointer events, so one code path serves mouse
  // and touch; `touch-action: none` on the grip keeps the page from scrolling instead.
  const onGripDown = (tab) => (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    const target = e.currentTarget;
    target.setPointerCapture?.(e.pointerId);
    setDrag({ tab, y: e.clientY, over: order.indexOf(tab), startY: e.clientY });
  };
  const indexAt = (y) => {
    const rows = order.map((t) => rowsRef.current[t]).filter(Boolean);
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i].getBoundingClientRect();
      if (y < r.top + r.height / 2) return i;
    }
    return rows.length - 1;
  };
  const onGripMove = (e) => {
    if (!drag) return;
    const over = indexAt(e.clientY);
    setDrag((d) => (d ? { ...d, y: e.clientY, over } : d));
  };
  const onGripUp = (e) => {
    if (!drag) return;
    const from = order.indexOf(drag.tab);
    // The release point decides, so a drag that never fired a move still lands where it ended.
    const to = e && Number.isFinite(e.clientY) ? indexAt(e.clientY) : drag.over;
    setDrag(null);
    if (from !== to && to >= 0) reorder(move(order, from, to));
  };

  const dragFrom = drag ? order.indexOf(drag.tab) : -1;
  const rowShift = (i) => {
    if (!drag || i === dragFrom) return 0;
    const h = rowsRef.current[drag.tab]?.getBoundingClientRect().height || 0;
    if (dragFrom < drag.over && i > dragFrom && i <= drag.over) return -h;
    if (dragFrom > drag.over && i < dragFrom && i >= drag.over) return h;
    return 0;
  };

  return (
    <ClubPanel phone={phone} style={{ gap: 4 }} data-testid="page-tabs-settings">
      <div style={{ paddingBottom: 8 }}>
        <PanelHead
          title="TABS ON YOUR PAGE"
          right={saving ? 'SAVING…' : saved ? null : `${visible.length} OF ${order.length} SHOWING`}
        >
          {saved && !saving && (
            <span className="flex items-center font-mono uppercase" style={{ gap: 6, padding: '3px 10px', borderRadius: 999, background: '#DCFCE7', color: '#166534', fontSize: 9, fontWeight: 800, letterSpacing: '.12em' }} data-testid="tabs-saved">
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M2 6 L5 9 L10 3" /></svg>SAVED
            </span>
          )}
        </PanelHead>
        <span style={{ display: 'block', paddingTop: 6, fontSize: 11, lineHeight: 1.55, color: MUTED }}>
          Drag the grip to put your tabs in order — the same order on phones and desktops. Pick the one your page opens on. Every tab has its own link.
        </span>
      </div>
      <div role="list" aria-label="Tabs in order" style={{ position: 'relative' }} onPointerMove={onGripMove} onPointerUp={onGripUp} onPointerCancel={onGripUp}>
        {order.map((t, i) => {
          const [label, desc] = TAB_META[t] || [t.toUpperCase(), ''];
          const locked = t === 'about';
          const on = locked ? true : !hidden.has(t);
          const isHome = on && t === homeTab;
          const lifting = drag?.tab === t;
          const link = slug ? (isHome ? `/pro/${slug}` : `/pro/${slug}/${t}`) : null;
          return (
            <div key={t} role="listitem" ref={(el) => { rowsRef.current[t] = el; }} className="flex items-center" data-testid={`tab-row-${t}`}
              style={{
                gap: phone ? 10 : 14, padding: '13px 0', borderTop: i ? RULE : 'none', opacity: on ? 1 : 0.55, background: '#fff',
                position: 'relative', zIndex: lifting ? 2 : 1,
                transform: lifting ? `translateY(${drag.y - drag.startY}px)` : `translateY(${rowShift(i)}px)`,
                transition: lifting ? 'none' : 'transform .15s',
                boxShadow: lifting ? '0 10px 30px rgba(0,0,0,.14)' : 'none', borderRadius: lifting ? 16 : 0,
              }}>
              <button type="button" aria-label={`Drag ${label}`} title="Drag to reorder" onPointerDown={onGripDown(t)} className="flex items-center justify-center"
                style={{ flexShrink: 0, width: 36, height: 36, border: 'none', borderRadius: '50%', background: lifting ? INK : CHIP, color: lifting ? '#fff' : SUBTLE, cursor: lifting ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }} data-testid={`tab-grip-${t}`}>
                <svg width="12" height="14" viewBox="0 0 12 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M1 3h10M1 7h10M1 11h10" /></svg>
              </button>
              <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
                <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.12em' }}>{label}</span>
                  {isHome && <TagPill bg={CYAN} size={8} data-testid={`tab-home-${t}`}>OPENS HERE</TagPill>}
                </span>
                <span style={{ fontSize: 11, color: MUTED }}>{desc}</span>
                {link && <span style={{ fontSize: 10, color: SUBTLE, wordBreak: 'break-all' }} data-testid={`tab-link-${t}`}>heyportal.app{link}</span>}
                {on && !isHome && (
                  <button type="button" onClick={() => makeHome(t)} className="self-start font-mono uppercase hover:opacity-70"
                    style={{ marginTop: 2, padding: '4px 10px', border: 'none', borderRadius: 999, background: CHIP, color: INK, fontSize: 9, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }} data-testid={`tab-make-home-${t}`}>
                    OPEN HERE FIRST
                  </button>
                )}
              </span>
              {!phone && (
                <span className="flex flex-col" style={{ gap: 4 }}>
                  <button type="button" onClick={() => nudge(i, -1)} aria-label={`Move ${label} up`} className="font-mono" style={{ width: 28, height: 28, border: 'none', borderRadius: '50%', background: CHIP, color: INK, opacity: i === 0 ? 0.3 : 1, fontSize: 10, cursor: 'pointer' }}>▲</button>
                  <button type="button" onClick={() => nudge(i, 1)} aria-label={`Move ${label} down`} className="font-mono" style={{ width: 28, height: 28, border: 'none', borderRadius: '50%', background: CHIP, color: INK, opacity: i === order.length - 1 ? 0.3 : 1, fontSize: 10, cursor: 'pointer' }}>▼</button>
                </span>
              )}
              <Switch on={on} disabled={locked} onChange={(next) => toggle(t, next)} label={label} testId={`tab-switch-${t}`} />
            </div>
          );
        })}
      </div>
      <span className="flex items-center" style={{ gap: 10, paddingTop: 12, borderTop: RULE }}>
        <TagPill bg={CHIP} size={8}>VISITORS</TagPill>
        <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>Turning a tab off hides it for visitors. Nothing is deleted.</span>
      </span>
    </ClubPanel>
  );
}
